// src/components/KeyValuesDashboard/components/tabs/InsightsTab.tsx

import { Lightbulb, TrendingUp, TrendingDown, AlertCircle, Award, Zap } from 'lucide-react';

import type { ComparisonResponse, Insight } from '@/types/analytics';
import { formatCurrency } from '@/utils/keyvalues-helpers';

interface InsightsTabProps {
  data: ComparisonResponse;
  mode: 'all' | 'compare';
}

export function InsightsTab({ data }: InsightsTabProps) {
  const insights = data.insights || [];

  // Agrupar insights por tipo
  const groupedInsights = insights.reduce((acc, insight) => {
    if (!acc[insight.type]) {
      acc[insight.type] = [];
    }
    acc[insight.type].push(insight);
    return acc;
  }, {} as Record<string, Insight[]>);

  // Configurações de estilo por tipo
  const insightStyles: Record<string, {
    icon: any;
    color: string;
    bgColor: string;
    borderColor: string;
    title: string;
  }> = {
    top_performer: {
      icon: Award,
      color: 'text-amber-600',
      bgColor: 'from-amber-50 to-orange-50',
      borderColor: 'border-amber-200',
      title: 'Top Performers',
    },
    significant_change: {
      icon: Zap,
      color: 'text-indigo-600',
      bgColor: 'from-indigo-50 to-purple-50',
      borderColor: 'border-indigo-200',
      title: 'Mudanças Significativas',
    },
    temporal_change: {
      icon: TrendingUp,
      color: 'text-violet-600',
      bgColor: 'from-violet-50 to-purple-50',
      borderColor: 'border-violet-200',
      title: 'Variações Temporais',
    },
    alert: {
      icon: AlertCircle,
      color: 'text-rose-600',
      bgColor: 'from-rose-50 to-red-50',
      borderColor: 'border-rose-200',
      title: 'Alertas',
    },
    growth: {
      icon: TrendingUp,
      color: 'text-emerald-600',
      bgColor: 'from-emerald-50 to-green-50',
      borderColor: 'border-emerald-200',
      title: 'Crescimento',
    },
    decline: {
      icon: TrendingDown,
      color: 'text-orange-600',
      bgColor: 'from-orange-50 to-amber-50',
      borderColor: 'border-orange-200',
      title: 'Declínio',
    },
  };

  // Se não há insights
  if (insights.length === 0) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-12">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mb-6">
            <Lightbulb className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Nenhum insight disponível
          </h3>
          <p className="text-sm text-slate-600 max-w-md">
            Não foram detectadas mudanças significativas ou padrões especiais neste período.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-indigo-600" />
              Insights Automáticos
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              Análises geradas automaticamente com base nos dados do período
            </p>
          </div>
          
          <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
            <span className="text-sm font-semibold text-indigo-900">
              {insights.length} insights encontrados
            </span>
          </div>
        </div>
      </div>

      {/* Insights Agrupados por Tipo */}
      {Object.entries(groupedInsights).map(([type, typeInsights]) => {
        const style = insightStyles[type] || insightStyles.alert;
        const Icon = style.icon;

        return (
          <div key={type} className="space-y-3">
            {/* Título da Categoria */}
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg bg-gradient-to-br ${style.bgColor} border ${style.borderColor}`}>
                <Icon className={`w-5 h-5 ${style.color}`} />
              </div>
              <h4 className="text-md font-bold text-slate-900">
                {style.title}
              </h4>
              <span className="text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded-full">
                {typeInsights.length}
              </span>
            </div>

            {/* Cards de Insights */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {typeInsights.map((insight, index) => (
                <InsightCard
                  key={index}
                  insight={insight}
                  style={style}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Resumo Executivo */}
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl shadow-lg border border-slate-300/50 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4">
          Resumo Executivo
        </h3>
        
        <div className="space-y-3 text-sm text-slate-700">
          {groupedInsights.top_performer && groupedInsights.top_performer.length > 0 && (
            <p>
              • <strong>Melhor Performance:</strong>{' '}
              {groupedInsights.top_performer[0].custom_value || 'N/A'}{' '}
              {groupedInsights.top_performer[0].revenue && (
                <>com {formatCurrency(groupedInsights.top_performer[0].revenue)} em receita</>
              )}
            </p>
          )}
          
          {groupedInsights.growth && groupedInsights.growth.length > 0 && (
            <p>
              • <strong>Crescimento Detectado:</strong>{' '}
              {groupedInsights.growth.length} {groupedInsights.growth.length === 1 ? 'valor apresentou' : 'valores apresentaram'} crescimento significativo
            </p>
          )}
          
          {groupedInsights.decline && groupedInsights.decline.length > 0 && (
            <p className="text-orange-700">
              • <strong>Atenção:</strong>{' '}
              {groupedInsights.decline.length} {groupedInsights.decline.length === 1 ? 'valor apresentou' : 'valores apresentaram'} declínio
            </p>
          )}
          
          {groupedInsights.alert && groupedInsights.alert.length > 0 && (
            <p className="text-rose-700">
              • <strong>Alertas:</strong>{' '}
              {groupedInsights.alert.length} {groupedInsights.alert.length === 1 ? 'alerta requer' : 'alertas requerem'} atenção
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// Componente de Card de Insight
interface InsightCardProps {
  insight: Insight;
  style: {
    icon: any;
    color: string;
    bgColor: string;
    borderColor: string;
  };
}

function InsightCard({ insight, style }: InsightCardProps) {
  const Icon = style.icon;

  return (
    <div className={`bg-gradient-to-br ${style.bgColor} rounded-xl shadow-md border ${style.borderColor} p-5 hover:shadow-lg transition-shadow`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <Icon className={`w-5 h-5 ${style.color}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Custom Value (se houver) */}
          {insight.custom_value && (
            <p className="text-sm font-semibold text-slate-900 mb-1 truncate" title={insight.custom_value}>
              {insight.custom_value}
            </p>
          )}
          
          {/* Mensagem */}
          <p className="text-sm text-slate-700 mb-2">
            {insight.message}
          </p>
          
          {/* Detalhes adicionais */}
          <div className="flex items-center gap-4 text-xs text-slate-600">
            {insight.change_percentage !== undefined && (
              <div className="flex items-center gap-1">
                {insight.trend === 'up' ? (
                  <TrendingUp className="w-3 h-3 text-emerald-600" />
                ) : insight.trend === 'down' ? (
                  <TrendingDown className="w-3 h-3 text-rose-600" />
                ) : null}
                <span className={
                  insight.trend === 'up' ? 'text-emerald-600 font-semibold' :
                  insight.trend === 'down' ? 'text-rose-600 font-semibold' :
                  'text-slate-600'
                }>
                  {insight.change_percentage > 0 ? '+' : ''}{insight.change_percentage.toFixed(1)}%
                </span>
              </div>
            )}
            
            {insight.metric && (
              <span className="text-slate-500">
                {insight.metric}
              </span>
            )}
            
            {insight.revenue !== undefined && (
              <span className="text-slate-600 font-medium">
                {formatCurrency(insight.revenue)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
